import {ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Router} from "@angular/router";
import {filter, Observable, Subject, Subscription, take, takeUntil} from "rxjs";
import {PopupService} from "../../services/popup.service";
import {AuthService} from "../../services/auth.service";
import {Store} from "@ngrx/store";
import {AppState} from "../../state/app.state";
import {SharedService} from "../../services/shared.service";
import {selectAllSearchesFailure, selectAllSearchesSuccess, selectArchiveReportFailure, selectArchiveReportSuccess, selectLoading, selectRestoreReportFailure, selectRestoreReportSuccess} from "../../state/search/search.selectors";
import {EventTypes} from "../../event-types";
import {allSearches, newSearch, archiveReport, restoreReport} from "../../state/search/search.actions";
import {map} from "rxjs/operators";
import {updateIsLoading} from "../../state/search/search.actions";
import {selectAreAllEventsComplete} from "../../state/search/search.selectors";
import { ReportListItem } from 'src/app/interfaces/report-list-item.interface'
import {HeaderService} from "../../services/header.service";
import {SidebarService} from "../../services/sidebar.service";
import {EchoService} from "../../services/echo.service";
import {updateReportStatusInSearches} from "../../state/search/search.actions";
import * as IcpActions from '../../state/icp/icp.actions';
import * as IcpSelectors from '../../state/icp/icp.selectors';
import * as SearchExportActions from '../../state/searchExport/search-export.actions';
import * as SearchExportSelectors from '../../state/searchExport/search-export.selectors';
import { DEFAULT_SORT, SortOption } from './reports-sort/reports-sort.component';
import { DEFAULT_DATE_RANGE, DateRange } from './reports-date-range/reports-date-range.component';
import { SearchPersonComponent } from '../../common/search-person/search-person.component';

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.scss'
})
export class ReportsComponent implements OnInit, OnDestroy {

  @ViewChild('inputElement') inputElement: ElementRef;
  @ViewChild('personSearch') personSearch: SearchPersonComponent;

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      this.inputElement.nativeElement.focus();
      event.preventDefault();  // Prevent any default action
    }
  }

  mediaQuery: MediaQueryList;

  //userSearches: any[] = [];

  pagination: any = {
    current_page: 1,
    from: 1,
    last_page: 1,
    total: 0,
    per_page: 10
  }

  perPage: number = 10;

  errorMessage = ""
  linkedin = ""

  filteredList$: Observable<any[]>;
  private processedReports: ReportListItem[] = [];

  // filteredList: any[] = [];

  loading: boolean = false;
  range:string = '10';
  archivedReports: boolean = false;

  searchQuery: string = '';

  user: any;

  // ICP-related properties (to check actual setup completion)
  userIcps: any[] = [];
  hasIcps: boolean = false;
  icpsLoading: boolean = true;

  showTooltip:[boolean] = [false];
  showArchivedSearchesTooltip:boolean = false;
  showGoBackToSearchesTooltip:boolean = false;
  windowHeight: number = 50;

  newSearchSuccessSubscription: Subscription = null;
  newSearchFailureSubscription: Subscription = null;

  allSearchesSuccessSubscription: Subscription = null;
  allSearchesFailureSubscription: Subscription = null;

  archiveSearchSuccessSubscription: Subscription = null;
  archiveSearchFailureSubscription: Subscription = null;

  restoreSearchSuccessSubscription: Subscription = null;
  restoreSearchFailureSubscription: Subscription = null;

  selectedFilters: { [key: string]: string[] } | null = null;

  currentSort: SortOption = { ...DEFAULT_SORT };
  currentDateRange: DateRange = DEFAULT_DATE_RANGE;

  exporting: boolean = false;

  // ----- Export selection -----
  // IDs of reports the user explicitly selected for export. Persisted alongside
  // the pagination state so the selection survives page / per-page / sort
  // changes (selection accumulates across pages). It is intentionally cleared
  // when the matching set changes (search / tags / date range / archived view)
  // so the backend's AND of search_report_ids with the active filters never
  // silently drops a selected report.
  selectedReportIds: Set<number> = new Set<number>();

  // Controls the export confirmation modal.
  showExportConfirm: boolean = false;

  // ----- Archive confirmation -----
  showArchiveConfirm: boolean = false;
  pendingArchiveId: number | null = null;
  pendingArchiveName: string = '';

  imageErrors: { [key: string]: boolean } = {};

  isMobile: boolean = false;

  private isIOS: boolean = /iPad|iPhone|iPod/.test(navigator.userAgent);

  isDesktop = window.innerWidth >= 1024;
  showMobileSidebar = false;

  showOverlay = false;

  // Add a key for storing pagination state
  private readonly PAGINATION_STATE_KEY = 'reports_pagination_state';

  // Subject for managing subscriptions cleanup
  private destroy$ = new Subject<void>();

  constructor(
    private popupService: PopupService,
    private authService : AuthService,
    private routerService: Router,
    private store: Store<AppState>,
    private sharedService: SharedService,
    private headerService: HeaderService,
    private sidebarService: SidebarService,
    private echoService: EchoService,
    private cdr: ChangeDetectorRef
  ) {
    this.mediaQuery = window.matchMedia('(max-width: 768px)');
  }

  ngOnInit(): void {

    // Show header with custom title
    this.headerService.setHeaderState('My Reports', null, false);

    if (this.isIOS) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
    }

    this.detectMobile()

    // Subscribe to the loading state from the store
    this.store.select(selectLoading).subscribe((loading) => {
      this.loading = loading;
    });

    // Wait for valid user before initializing (best practice pattern)
    this.authService.authUser()
      .pipe(
        filter(user => {
          const isValid = user !== null && user !== undefined;
          return isValid;
        }), // Wait for non-null user
        take(1), // Take only the first valid emission
        takeUntil(this.destroy$) // Cleanup on destroy
      )
      .subscribe({
        next: (user) => {
          this.user = user;

          // Load user's ICPs to check actual setup completion (not relying on has_completed_setup flag)
          this.loadUserIcps();

          // Setup Echo channel for real-time updates
          this.echoService.getEcho().private('ReportChannel.' + user.id)
            .listen('.search-report-completed', (reportEvent: any) => {
              const reportId = Number(reportEvent.id || reportEvent.search_id);

              if (isNaN(reportId)) {
                console.error('Invalid report ID in the event', reportEvent);
                return;
              }

              this.store.dispatch(updateReportStatusInSearches({
                reportId: reportId,
                statusId: 3
              }));

              // The status patch above only flips search_status_id locally — it doesn't
              // carry the freshly-computed ICP score, so re-fetch the list once from the
              // API so the finished report's row (icp_score included) is accurate.
              this.allSearches(this.pagination.current_page, this.perPage, this.searchQuery, this.archivedReports);
            });
        },
        error: (error) => {
          console.error('[reports] Error loading user:', error);
        }
      });

    this.sharedService.eventEmitter.subscribe(event => {
      if (event.type === EventTypes.RELOAD_REPORTS) {
        this.allSearches(this.pagination.current_page, this.perPage, this.searchQuery, this.archivedReports);
      }
    });

    this.updateHeight();

    this.getFilteredList()

    // Listen for report finished event
    this.sharedService.eventEmitter.subscribe((event) => {
      const eventType = event.type;

      if (eventType === 'REPORT_FINISHED') {
        this.allSearches(this.pagination.current_page, this.perPage, this.searchQuery, this.archivedReports);
      }
    });

    this.checkIfDesktop();

    // Subscribe to export loading state
    this.store.select(SearchExportSelectors.selectStartExportLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => {
        this.exporting = loading;
      });

    // When an export is successfully queued while the confirmation modal is
    // open, close the modal and clear the selection.
    this.store.select(SearchExportSelectors.selectStartExportResult)
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result && this.showExportConfirm) {
          this.showExportConfirm = false;
          this.clearSelection();
        }
      });

  }

  /**
   * Load user's ICPs to check actual setup completion
   * This is the source of truth instead of has_completed_setup flag
   */
  private loadUserIcps(): void {
    this.icpsLoading = true;

    // Dispatch action to load all ICPs
    this.store.dispatch(IcpActions.loadIcps({
      page: 1,
      perPage: 100 // Load all ICPs
    }));

    // Subscribe to ICP list
    this.store.select(IcpSelectors.selectIcps)
      .pipe(takeUntil(this.destroy$))
      .subscribe(icps => {
        this.userIcps = icps || [];
        this.hasIcps = this.userIcps.length > 0;
        this.icpsLoading = false;

        // Check if user has completed setup (has at least one ICP)
        if (!this.hasIcps) {
          // User has no ICPs, redirect to ICP creation
          // Uncomment this when ready to enforce:
          // this.routerService.navigate(['/icp/create']);
          return;
        }

        // User has ICPs - proceed with loading reports

        // Restore pagination state if exists
        this.restorePaginationState();

        // Load searches with restored pagination
        this.allSearches(this.pagination.current_page, this.perPage, this.searchQuery, this.archivedReports);
      });
  }

  /**
   * Save pagination state to sessionStorage
   */
  private savePaginationState(): void {
    const state = {
      currentPage: this.pagination.current_page,
      perPage: this.perPage,
      searchQuery: this.searchQuery,
      archivedReports: this.archivedReports,
      selectedFilters: this.selectedFilters,
      sort: this.currentSort,
      dateRange: this.currentDateRange,
      selectedReportIds: Array.from(this.selectedReportIds)
    };

    sessionStorage.setItem(this.PAGINATION_STATE_KEY, JSON.stringify(state));
  }

  /**
   * Restore pagination state from sessionStorage
   */
  private restorePaginationState(): void {
    const savedState = sessionStorage.getItem(this.PAGINATION_STATE_KEY);

    if (savedState) {
      try {
        const state = JSON.parse(savedState);

        // Restore the state
        this.pagination.current_page = state.currentPage || 1;
        this.perPage = state.perPage || 10;
        this.searchQuery = state.searchQuery || '';
        this.archivedReports = state.archivedReports || false;
        this.selectedFilters = state.selectedFilters || null;
        if (state.sort && state.sort.sort_by && state.sort.sort_dir) {
          this.currentSort = { sort_by: state.sort.sort_by, sort_dir: state.sort.sort_dir };
        }
        if (state.dateRange) {
          this.currentDateRange = state.dateRange;
        }
        if (Array.isArray(state.selectedReportIds)) {
          this.selectedReportIds = new Set<number>(state.selectedReportIds);
        }

        // Update pagination object
        this.pagination.per_page = this.perPage;

      } catch (error) {
        console.error('Error restoring pagination state:', error);
        // If there's an error, clear the saved state
        sessionStorage.removeItem(this.PAGINATION_STATE_KEY);
      }
    }
  }

  /**
   * Clear pagination state from sessionStorage
   */
  private clearPaginationState(): void {
    sessionStorage.removeItem(this.PAGINATION_STATE_KEY);
  }

  checkIfDesktop(): void {
    this.isDesktop = window.innerWidth >= 1024;
  }

  onDropdownVisibility(isVisible: boolean) {
    this.showOverlay = isVisible;
  }

  /**
   * The overlay shown while the person-search dropdown is open swallows clicks on the
   * report list, so clicking it must dismiss the dropdown rather than do nothing.
   * (search-person also closes itself on any outside click; this keeps the overlay
   * honest even if that click never reaches the document.)
   */
  dismissPersonSearch(): void {
    this.personSearch?.closeDropdown();
    this.showOverlay = false;
  }

  getFilteredList() {

    // Listen for all events completion for all reports
    this.filteredList$ = this.store.select(selectAllSearchesSuccess).pipe(
      filter((response) => response !== null),
      map((response) => {
        const reports = response.data || [];

        // Monitor event statuses for each report
        reports.forEach((report) => {
          this.store
            .select(selectAreAllEventsComplete(report.id))
            .pipe(take(1)) // Automatically unsubscribe after the first value
            .subscribe((allComplete) => {
              if (allComplete) {
                // Update the `is_loading` property for the report
                this.store.dispatch(
                  updateIsLoading({ searchId: report.id, isLoading: false })
                );
              }
            });
        });

        return reports;
      })
    );

  }

  // Modify the onFiltersChanged method to update selected filters
  onFiltersChanged(filters: { [key: string]: string[] }): void {
    this.selectedFilters = filters;
    // Changing the tag filters changes which reports match — clear the export
    // selection so we never silently drop reports that fall out of the filter
    // (the backend ANDs search_report_ids with the active filters).
    this.selectedReportIds.clear();
    this.pagination.current_page = 1; // Reset to first page when filters change
    this.allSearches(1, this.perPage, this.searchQuery, this.archivedReports); // Call the search with the updated filters
  }

  // Sort change handler — reset to first page and reload with the new sort.
  onSortChanged(sort: SortOption): void {
    this.currentSort = sort;
    this.pagination.current_page = 1;
    this.allSearches(1, this.perPage, this.searchQuery, this.archivedReports);
  }

  // Date-range change — applies on top of the existing sort and tag filters.
  onDateRangeChanged(range: DateRange): void {
    this.currentDateRange = range;
    // Date range changes the matching set — clear the export selection.
    this.selectedReportIds.clear();
    this.pagination.current_page = 1;
    this.allSearches(1, this.perPage, this.searchQuery, this.archivedReports);
  }

  /**
   * All searches.
   *
   * @param page number
   * @param search string
   */
  allSearches(page: number = 1, perPage: number = 10, search: string = '',archived: boolean = false): void {
    if (this.newSearchSuccessSubscription) {
      this.newSearchSuccessSubscription.unsubscribe();
    }
    if (this.newSearchFailureSubscription) {
      this.newSearchFailureSubscription.unsubscribe();
    }

    // Set loading to true before fetching data
    // this.loading = true;
    // this.cdr.detectChanges();

    // Include tags only if selectedFilters is not null
    const actionPayload = {
      page: page,
      perPage: perPage,
      search: search,
      tags: this.selectedFilters !== null ? this.selectedFilters : undefined,
      archived:archived,
      sortBy: this.currentSort.sort_by,
      sortDir: this.currentSort.sort_dir,
      dateRange: this.currentDateRange,
    };

    this.store.dispatch(allSearches(actionPayload));

    // Listen for the all searches success or failure from the store
    const allSearchesSuccess$ = this.store.select(selectAllSearchesSuccess);
    const allSearchesFailure$ = this.store.select(selectAllSearchesFailure);

    if (this.allSearchesSuccessSubscription) {
      this.allSearchesSuccessSubscription.unsubscribe();
    }

    // Subscribe to the all searches success
    this.allSearchesSuccessSubscription = allSearchesSuccess$.pipe(
      filter(response => response !== null)
    ).subscribe({
      next: (response) => {
        // Handle success
        const reports = response.data || [];
        if (reports) {

          // Update processed reports and filteredList$
          this.processedReports = (response.data || []).map((report: any) => ({
            id: report.id,
            name: report.name || 'Unknown',
            title: report.search_person?.enhanced_data?.title,
            picture: report.search_person?.profile_picture || 'https://ui-avatars.com/api/?background=8b5cf6&color=fff&rounded=true&name='+report.name+'&format=svg&size=128', // report.picture
            country: report.country || null,
            is_archived: report.is_archived || false,
            is_shared: report.is_shared || false,
            shared_by: report.shared_by || null,
            workspace: report.workspace || null,
            company: report.company ? {
              name: report.company.name || 'Unknown Company',
              logo_url: report.company.enhanced_data?.logo_url || null,
              city: report.company.enhanced_data?.city || null,
              country: report.company.enhanced_data?.country || null,
              icp_score: report.company.icp_score || 0,
            } : null,
            updated_at_human: report.updated_at_human || 'Unknown Date',
            // Show the created date (humanized server-side, like updated_at_human) so the
            // displayed date matches the created_at sort.
            created_at_human: report.created_at_human || 'Unknown Date',
            search_status_id: report.search_status_id
          }));

          this.filteredList$ = new Observable((observer) => {
            observer.next(this.processedReports);
            observer.complete();
          });

          // Update pagination metadata
          this.pagination = {
            current_page: response.current_page,
            total: response.total,
            last_page: response.last_page,
            per_page: response.per_page,
            from: response.from,
            to: response.to
          };

          // Save pagination state after successful load
          this.savePaginationState();

        }

        /*
        this.pagination = { ...response }; // Create a new object to trigger change detection

        // Check event completion for each report
        reports.forEach((report) => {
          this.store
            .select(selectAreAllEventsComplete(report.id))
            .pipe(take(1))
            .subscribe((allComplete) => {
              if (allComplete) {
                // Update is_loading for the report
                this.store.dispatch(
                  updateIsLoading({ searchId: report.id, isLoading: false })
                );
              }
            });
        });
        */
      },
      error: (error) => {
        // Handle error
        console.error('Error loading searches:', error);

        // Set loading to false on error
        // this.loading = false;
        // this.cdr.detectChanges();

        if (this.allSearchesSuccessSubscription) {
          this.allSearchesSuccessSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
        }
      }
    });

    // Subscribe to the search failure
    this.allSearchesFailureSubscription = allSearchesFailure$.pipe(
      filter(failure => failure !== null),
      take(1)
    ).subscribe({
      next: (error) => {
        // Handle failure
        const errorMessage = error.error.message;

        // we show the popup notify from search-person component
        // this.popupService.notify(errorMessage, 'Error', 'error');

        // Set loading to false on failure
        // this.loading = false;
        // this.cdr.detectChanges();

        if (this.allSearchesFailureSubscription) {
          this.allSearchesFailureSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
        }
      },
      error: (error) => {
        // Handle error in error handling
        console.error('Error handling error:', error);

        // Set loading to false on error in error handling
        // this.loading = false;
        // this.cdr.detectChanges();

        if (this.allSearchesFailureSubscription) {
          this.allSearchesFailureSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
        }
      }
    });
  }

  ngOnDestroy(): void {

    // Complete destroy$ subject to cleanup all takeUntil subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    if (this.isIOS) {
      document.body.style.overflow = '';
      document.body.style.position = '';
    }

    if (this.newSearchSuccessSubscription) {
      this.newSearchSuccessSubscription.unsubscribe();
    }
    if (this.newSearchFailureSubscription) {
      this.newSearchFailureSubscription.unsubscribe();
    }

    if (this.allSearchesSuccessSubscription) {
      this.allSearchesSuccessSubscription.unsubscribe();
    }
    if (this.allSearchesFailureSubscription) {
      this.allSearchesFailureSubscription.unsubscribe();
    }

    // Reset filters, search, and pagination on exit so returning opens the default list
    this.clearPaginationState();
  }

  @HostListener('touchmove', ['$event'])
  disableTouch(event: TouchEvent) {
    if (this.isIOS) {
      const isInsideUl = (event.target as HTMLElement).closest('ul');
      if (!isInsideUl) {
        event.preventDefault(); // Prevent drag/scroll outside ul
      }
    }
  }

  /**
   * Page change.
   * This function is triggered when the user changes the page.
   */
  onPageChanged(page: number): void {
    this.pagination.current_page = page;
    this.allSearches(page, this.perPage, this.searchQuery, this.archivedReports);
  }

  /**
   * Per page change.
   * This function is triggered when the user changes the per page.
   */
  onPerPageChanged(perPage: number): void {

    this.perPage = perPage

    this.pagination.per_page = perPage;
    this.pagination.current_page = 1;
    this.allSearches(1, perPage, this.searchQuery, this.archivedReports);

  }

  viewArchivedReports() {
    this.archivedReports = true;
    this.searchQuery = ''
    this.selectedReportIds.clear(); // Switching archived/active view changes the set
    this.pagination.current_page = 1; // Reset to first page
    this.allSearches(1,this.perPage,'',true);
    return true;
  }

  restoreSearch(reportId,event) {

    event.stopPropagation();

    this.store.dispatch(restoreReport({
      reportId:reportId
    }));

    // Listen for the all searches success or failure from the store
    const restoreSearchSuccess$ = this.store.select(selectRestoreReportSuccess);
    const restoreSearchFailure$ = this.store.select(selectRestoreReportFailure);

    if (this.restoreSearchSuccessSubscription) {
      this.restoreSearchSuccessSubscription.unsubscribe();
    }

    // Subscribe to the all searches success
    this.restoreSearchSuccessSubscription = restoreSearchSuccess$.pipe(
      filter(response => response !== null),
      take(1)
    ).subscribe({
      next: (response) => {
        this.popupService.notify('Search restored Successfully!', 'Restored search', 'success');
        // Drop the restored report from the export selection — it no longer matches this view.
        this.selectedReportIds.delete(reportId);
        this.reloadAfterMutation();
      },
      error: (error) => {
        // Handle error
        console.error('Error at archiving search:', error);

        // Set loading to false on error
        // this.loading = false;
        // this.cdr.detectChanges();

        if (this.restoreSearchSuccessSubscription) {
          this.restoreSearchSuccessSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
        }
      }
    });

    // Subscribe to the search failure
    this.restoreSearchFailureSubscription = restoreSearchFailure$.pipe(
      filter(failure => failure !== null),
      take(1)
    ).subscribe({
      next: (error) => {
        // Handle failure
        const errorMessage = error.error.message;
        this.popupService.notify(errorMessage, 'Error', 'error');

        // Set loading to false on failure
        // this.loading = false;
        // this.cdr.detectChanges();

        if (this.restoreSearchFailureSubscription) {
          this.restoreSearchFailureSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
        }
      },
      error: (error) => {
        // Handle error in error handling
        console.error('Error handling error:', error);

        // Set loading to false on error in error handling
        // this.loading = false;
        // this.cdr.detectChanges();

        if (this.restoreSearchFailureSubscription) {
          this.restoreSearchFailureSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
        }
      }
    });

  }
  /**
   * Archive button clicked — open the confirmation modal instead of archiving
   * immediately. Stops propagation so the row's routerLink does not navigate.
   */
  promptArchive(reportId: number, name: string, event: Event): void {
    event.stopPropagation();
    this.pendingArchiveId = reportId;
    this.pendingArchiveName = name || '';
    this.showArchiveConfirm = true;
  }

  /** User dismissed the archive confirmation. */
  cancelArchive(): void {
    this.showArchiveConfirm = false;
    this.pendingArchiveId = null;
    this.pendingArchiveName = '';
  }

  /** User confirmed — archive the pending report. */
  confirmArchive(): void {
    const reportId = this.pendingArchiveId;
    this.showArchiveConfirm = false;
    this.pendingArchiveName = '';
    this.pendingArchiveId = null;

    if (reportId != null) {
      this.archiveSearch(reportId);
    }
  }

  /**
   * Reload the list after archiving/restoring a report, keeping the user on the
   * same page (and same per-page / search / archived filters) instead of jumping
   * back to page 1. If the mutated report was the last item on the current page,
   * step back one page so we don't land on an empty page. allSearches() persists
   * the resulting pagination state to sessionStorage on success.
   */
  private reloadAfterMutation(): void {
    let page = this.pagination.current_page || 1;
    if (page > 1 && this.processedReports.length <= 1) {
      page = page - 1;
    }
    this.allSearches(page, this.perPage, this.searchQuery, this.archivedReports);
  }

  archiveSearch(reportId) {
    this.store.dispatch(archiveReport({
      reportId:reportId
    }));

    // Listen for the all searches success or failure from the store
    const archiveSearchSuccess$ = this.store.select(selectArchiveReportSuccess);
    const archiveSearchFailure$ = this.store.select(selectArchiveReportFailure);

    if (this.archiveSearchSuccessSubscription) {
      this.archiveSearchSuccessSubscription.unsubscribe();
    }

    // Subscribe to the all searches success
    this.archiveSearchSuccessSubscription = archiveSearchSuccess$.pipe(
      filter(response => response !== null),
      take(1)
    ).subscribe({
      next: (response) => {
        this.popupService.notify('Search archived Successfully!', 'Archived search', 'success');
        // Drop the archived report from the export selection so it can't be exported as a stale id.
        this.selectedReportIds.delete(reportId);
        this.reloadAfterMutation();
      },
      error: (error) => {
        // Handle error
        console.error('Error at archiving search:', error);
        this.reloadAfterMutation();
        // Set loading to false on error
        // this.loading = false;
        // this.cdr.detectChanges();

        if (this.archiveSearchSuccessSubscription) {
          this.archiveSearchSuccessSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
        }
      }
    });

    // Subscribe to the search failure
    this.archiveSearchFailureSubscription = archiveSearchFailure$.pipe(
      filter(failure => failure !== null),
      take(1)
    ).subscribe({
      next: (error) => {
        // Handle failure
        const errorMessage = error.error.message;
        this.popupService.notify(errorMessage, 'Error', 'error');

        // Set loading to false on failure
        // this.loading = false;
        // this.cdr.detectChanges();

        if (this.archiveSearchFailureSubscription) {
          this.archiveSearchFailureSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
        }
      },
      error: (error) => {
        // Handle error in error handling
        console.error('Error handling error:', error);

        // Set loading to false on error in error handling
        // this.loading = false;
        // this.cdr.detectChanges();

        if (this.archiveSearchFailureSubscription) {
          this.archiveSearchFailureSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
        }
      }
    });

  }

  /**
   * Refresh reports.
   * This function is triggered when the user clicks the 'Refresh' button.
   */
  refreshReports () {
    this.archivedReports = false;
    this.searchQuery = ''
    this.pagination.current_page = 1; // Reset to first page
    this.perPage = 10; // Reset to default per page
    this.selectedFilters = null; // Clear filters
    this.selectedReportIds.clear(); // Clear export selection
    this.clearPaginationState(); // Clear saved state
    this.allSearches(1, 10, '', false);
  }

  /**
   * Search report.
   * This function is triggered when the user clicks the 'Search' button.
   */
  searchReport () {
    this.selectedReportIds.clear(); // Text search changes the matching set
    this.pagination.current_page = 1; // Reset to first page when searching
    this.allSearches(1, this.perPage, this.searchQuery, this.archivedReports);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.updateHeight();

    const wasDesktop = this.isDesktop;
    this.checkIfDesktop();
  }

  updateHeight() {
    const remSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
    const filterHeight = 52;
    this.windowHeight = window.innerHeight - (22.2 * remSize) - filterHeight;
  }

  onCompanyImageError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = 'assets/images/human.svg'; // Fallback image when error occurs
  }

  onImageError(reportId: number) {
    this.imageErrors[reportId] = true;
  }

  private detectMobile() {
    // Example mobile detection - adjust breakpoint as needed
    this.isMobile = window.innerWidth < 768;
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 768;
    });
  }

  toggleMobileSidebar(): void {
    this.sidebarService.toggleMobileSidebar();
  }

  // ============================================
  // Export selection
  // ============================================

  /** Number of reports currently selected for export. */
  get selectedCount(): number {
    return this.selectedReportIds.size;
  }

  /** Whether the current page has any reports (drives the select-all bar visibility). */
  get hasReports(): boolean {
    return this.processedReports.length > 0;
  }

  /** Whether a given report is selected. */
  isReportSelected(reportId: number): boolean {
    return this.selectedReportIds.has(reportId);
  }

  /**
   * Toggle a single report's selection. Called from the row checkbox; the
   * event is swallowed so it does not trigger the row's routerLink navigation.
   */
  toggleReportSelection(reportId: number, event: Event): void {
    event.stopPropagation();

    if (this.selectedReportIds.has(reportId)) {
      this.selectedReportIds.delete(reportId);
    } else {
      this.selectedReportIds.add(reportId);
    }

    // Persist immediately so the selection survives page changes.
    this.savePaginationState();
  }

  /** True when every report on the current page is selected (and the page is non-empty). */
  areAllOnPageSelected(): boolean {
    return this.processedReports.length > 0
      && this.processedReports.every(report => this.selectedReportIds.has(report.id));
  }

  /** True when some — but not all — reports on the current page are selected (indeterminate state). */
  isSomeOnPageSelected(): boolean {
    return !this.areAllOnPageSelected()
      && this.processedReports.some(report => this.selectedReportIds.has(report.id));
  }

  /**
   * Select-all toggle for the current page. Selecting adds every visible report
   * to the (accumulating) selection; deselecting removes only the visible ones,
   * leaving selections made on other pages intact.
   */
  toggleSelectAllOnPage(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;

    this.processedReports.forEach(report => {
      if (checked) {
        this.selectedReportIds.add(report.id);
      } else {
        this.selectedReportIds.delete(report.id);
      }
    });

    this.savePaginationState();
  }

  /** Clear the entire selection across all pages. */
  clearSelection(): void {
    this.selectedReportIds.clear();
    this.savePaginationState();
  }

  // ============================================
  // Export flow (confirmation modal)
  // ============================================

  /** Export button clicked — open the confirmation modal. */
  exportReports(): void {
    // Start from a clean export result so the success watcher only reacts to
    // this run.
    this.store.dispatch(SearchExportActions.resetStartExportState());
    this.showExportConfirm = true;
  }

  /** User cancelled / dismissed the confirmation modal. */
  cancelExport(): void {
    this.showExportConfirm = false;
  }

  /** User confirmed the export — dispatch with the explicit selection (if any). */
  confirmExport(): void {
    const ids = Array.from(this.selectedReportIds);

    this.store.dispatch(SearchExportActions.startSearchExport({
      search: this.searchQuery || undefined,
      tags: this.selectedFilters,
      archived: this.archivedReports,
      sort_by: this.currentSort.sort_by,
      sort_dir: this.currentSort.sort_dir,
      date_range: this.currentDateRange,
      search_report_ids: ids.length > 0 ? ids : undefined
    }));
  }

}
